import React, { Component } from "react";
import { withRouter } from "react-router-dom";
import PropTypes from "prop-types";
import withStyles from "@material-ui/core/styles/withStyles";
import InputAdornment from "@material-ui/core/InputAdornment";
import Icon from "@material-ui/core/Icon";
import CircularProgress from "@material-ui/core/CircularProgress";
import Chip from "@material-ui/core/Chip";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import DialogFooter from "components/Dialog/DialogFooter.jsx";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import InputLabel from "@material-ui/core/InputLabel";
import FormControl from "@material-ui/core/FormControl";

import GridContainer from "components/Grid/GridContainer.jsx";
import GridItem from "components/Grid/GridItem.jsx";
import Button from "components/CustomButtons/Button.jsx";
import CustomInput from "components/CustomInput/CustomInput.jsx";
import DataTable, { Pill } from "components/DataTable/DataTable.jsx";

import api from "api/index";
import ViewToolbar from "components/Toolbar/ViewToolbar.jsx";
import CustomDatePicker from "components/DatePicker/CustomDatePicker.jsx";
import DialogHeader from "components/Dialog/DialogHeader.jsx";

const STAT_CONFIGS = [
  { key: "total",     label: "Total Students", icon: "groups",        bg: "#e3f2fd", text: "#0d47a1", bar: "#1565c0" },
  { key: "active",    label: "Active",         icon: "person_check",  bg: "#e8f5e9", text: "#2e7d32", bar: "#388e3c" },
  { key: "deferred",  label: "Deferred",       icon: "pause_circle",  bg: "#fff8e1", text: "#e65100", bar: "#ef6c00" },
  { key: "completed", label: "Completed",      icon: "school",        bg: "#e0f2f1", text: "#00695c", bar: "#00796b" }
];

const styles = {
  heroWrap: {
    position: "relative",
    width: "100%",
    height: "220px",
    borderRadius: "20px",
    overflow: "hidden",
    marginBottom: "30px",
    background: "linear-gradient(160deg, #1a237e 0%, #283593 60%, #3949ab 100%)",
    boxShadow: "0 20px 40px -15px rgba(26, 35, 126, 0.3)"
  },
  heroOverlay: {
    position: "absolute",
    inset: 0,
    background: "linear-gradient(to bottom, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)",
    zIndex: 1
  },
  heroContent: {
    position: "absolute",
    inset: 0,
    zIndex: 2,
    padding: "40px",
    display: "flex",
    flexDirection: "column",
    justifyContent: "flex-end",
    color: "#fff"
  },
  heroTitle: { fontSize: "32px", fontWeight: 800, margin: 0, letterSpacing: "-0.5px" },
  heroSub: { fontSize: "15px", margin: "8px 0 0", opacity: 0.9, fontWeight: 400 },

  // KPI stat cards
  kpiRow: { display: "flex", gap: "16px", marginBottom: "32px", flexWrap: "wrap" },
  statCard: {
    flex: 1,
    minWidth: "180px",
    padding: "20px",
    borderRadius: "16px",
    background: "#fff",
    boxShadow: "0 4px 20px 0 rgba(0,0,0,0.05)",
    display: "flex",
    alignItems: "center",
    gap: "16px",
    transition: "transform 0.2s ease",
    border: "1px solid #f1f5f9",
    "&:hover": { transform: "translateY(-4px)", boxShadow: "0 12px 30px 0 rgba(0,0,0,0.08)" }
  },
  iconBox: {
    width: "48px",
    height: "48px",
    borderRadius: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    flexShrink: 0
  },
  statNumber: { fontSize: "20px", fontWeight: 800, color: "#1e293b", margin: 0 },
  statLabel: { fontSize: "12px", color: "#64748b", margin: "2px 0 0", fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.4px" },

  // Toolbar & Search
  toolbarWrap: {
    background: "#fff",
    padding: "20px",
    borderRadius: "16px",
    marginBottom: "24px",
    boxShadow: "0 2px 12px 0 rgba(0,0,0,0.03)",
    border: "1px solid #f1f5f9"
  },
  searchBar: {
    display: "flex",
    gap: "12px",
    marginTop: "16px",
    alignItems: "center",
    flexWrap: "wrap"
  },
  searchInput: {
    flex: 1,
    minWidth: "280px",
    "& .MuiInputBase-root": {
      borderRadius: "10px",
      background: "#f8fafc",
      padding: "4px 12px",
      border: "1px solid #e2e8f0",
      transition: "all 0.2s ease",
      "&:hover": { borderColor: "#cbd5e1" },
      "&.Mui-focused": { background: "#fff", borderColor: "#3f51b5", boxShadow: "0 0 0 4px rgba(63, 81, 181, 0.1)" }
    },
    "& .MuiInput-underline:before, & .MuiInput-underline:after": { display: "none" }
  },
  filterSelect: {
    height: "45px",
    minWidth: "160px",
    borderRadius: "10px",
    padding: "0 12px",
    border: "1px solid #e2e8f0",
    background: "#f8fafc",
    fontSize: "14px",
    color: "#475569",
    outline: "none",
    cursor: "pointer",
    "&:focus": { borderColor: "#3f51b5", background: "#fff" }
  },

  tableCard: {
    borderRadius: "16px",
    boxShadow: "0 4px 24px 0 rgba(0,0,0,0.04)",
    border: "1px solid #f1f5f9"
  },
  tableHeader: {
    padding: "20px 24px",
    borderBottom: "1px solid #f1f5f9",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center"
  },
  tableTitle: { fontSize: "18px", fontWeight: 700, color: "#1e293b", margin: 0 },
  tableSubtitle: { fontSize: "13px", color: "#64748b", margin: "2px 0 0" },

  statusChip: {
    fontSize: "11px",
    height: "24px",
    fontWeight: 600,
    borderRadius: "6px",
    textTransform: "capitalize"
  },
  link: {
    color: "#3f51b5",
    cursor: "pointer",
    fontWeight: "600",
    textDecoration: "none",
    "&:hover": { textDecoration: "underline" }
  },
  pagination: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    padding: "16px 24px",
    borderTop: "1px solid #f1f5f9"
  },
  pageInfo: { fontSize: "13px", color: "#64748b", fontWeight: 500 },
  actionBtn: { borderRadius: "8px", textTransform: "none", fontWeight: 600 },
  
  emptyState: {
    textAlign: "center",
    padding: "60px 20px",
    color: "#64748b"
  },
  formControl: { width: "100%", marginTop: "16px" },
  sectionTitle: { color: "#1a237e", fontWeight: "bold", fontSize: "13px", borderBottom: "1px solid #e0e0e0", paddingBottom: "4px", marginTop: "16px", marginBottom: "4px" },
  errorMsg: { color: "#f44336", fontSize: "13px", marginBottom: "8px" },
  successMsg: { color: "#4caf50", fontSize: "13px", marginBottom: "8px" }
};

const STATUS_TONE = {
  active: "success",
  deferred: "warning",
  withdrawn: "danger",
  completed: "info"
};

const BLANK_FORM = {
  firstName: "", lastName: "", dob: "", gender: "",
  nationality: "Ugandan", email: "", phone: "", address: "",
  guardianName: "", guardianPhone: "",
  programmeId: "", yearOfStudy: "1", enrollmentDate: "", status: "active"
};

const GENDERS = ["Male", "Female"];
const NATIONALITIES = ["Ugandan", "Kenyan", "Tanzanian", "Rwandan", "Other"];

class StudentList extends Component {
  state = {
    tab: 0,
    students: [], total: 0, page: 1, totalPages: 1,
    search: "", programme: "", status: "active",
    loading: true, error: null,
    // registration dialog
    open: false,
    form: { ...BLANK_FORM },
    programmes: [],
    saving: false,
    formError: null,
    formSuccess: null
  };

  componentDidMount() {
    this.fetchStudents();
    this.fetchProgrammes();
  }

  fetchStudents = async () => {
    this.setState({ loading: true });
    const { page, search, programme, status } = this.state;
    try {
      const res = await api.get("/students", {
        params: { page, limit: 20, search, programme, status: status || undefined }
      });
      const d = res.data.data || res.data;
      this.setState({
        students: d.data || d,
        total: d.total || (d.data || d).length,
        totalPages: d.totalPages || 1,
        loading: false
      });
    } catch (e) {
      this.setState({ error: "Could not load students.", loading: false });
    }
  };

  fetchProgrammes = async () => {
    try {
      const res = await api.get("/programmes?limit=100");
      const d = res.data.data || res.data;
      this.setState({ programmes: Array.isArray(d) ? d : d.data || [] });
    } catch (e) { /* non-fatal */ }
  };

  handleSearch = e => this.setState({ search: e.target.value });
  handleFilter = field => e => this.setState({ [field]: e.target.value }, this.fetchStudents);
  handleSearchSubmit = e => { e.preventDefault(); this.setState({ page: 1 }, this.fetchStudents); };
  goToPage = p => this.setState({ page: p }, this.fetchStudents);

  handlePaginationChange = updaterOrValue => {
    const { page } = this.state;
    const current = { pageIndex: page - 1, pageSize: 20 };
    const next = typeof updaterOrValue === "function" ? updaterOrValue(current) : updaterOrValue;
    this.goToPage(next.pageIndex + 1);
  };

  openDialog = () => this.setState({ open: true, form: { ...BLANK_FORM }, formError: null, formSuccess: null });
  closeDialog = () => this.setState({ open: false, saving: false });

  handleFormChange = field => e => {
    const value = e?.target?.value ?? "";
    this.setState(prev => ({ form: { ...prev.form, [field]: value }, formError: null }));
  };

  handleRegister = async () => {
    const { form } = this.state;
    const firstName = form.firstName.trim();
    const lastName = form.lastName.trim();
    if (!firstName || !lastName) {
      this.setState({ formError: "First name and last name are required." }); return;
    }
    if (!form.gender) {
      this.setState({ formError: "Gender is required." }); return;
    }
    if (!form.programmeId) {
      this.setState({ formError: "Programme is required." }); return;
    }
    this.setState({ saving: true, formError: null });
    try {
      await api.post("/students", { ...form, firstName, lastName });
      this.setState({ formSuccess: "Student registered successfully!", saving: false });
      setTimeout(() => {
        this.closeDialog();
        this.fetchStudents();
      }, 1200);
    } catch (err) {
      const msg = err.response?.data?.errors
        ? err.response.data.errors.map(e => e.msg).join(", ")
        : err.response?.data?.message || "Failed to register student.";
      this.setState({ formError: msg, saving: false });
    }
  };

  render() {
    const { classes, history } = this.props;
    const {
      students, loading, error, search, status, page, totalPages, total,
      open, form, programmes, saving, formError, formSuccess
    } = this.state;

    const columns = [
      {
        id: "studentNo",
        header: "Student No",
        meta: { variant: "mono" },
        cell: info => (
          <span className={classes.link} onClick={() => history.push(`/bwillis/students/${info.row.original.id}`)}>
            {info.row.original.studentNo}
          </span>
        )
      },
      {
        id: "name",
        header: "Full Name",
        cell: info => (
          <div style={{ fontWeight: 600, color: "#1e293b" }}>{`${info.row.original.firstName} ${info.row.original.lastName}`}</div>
        )
      },
      { id: "programme", header: "Programme", accessorFn: s => s.programme?.name || s.programmeId || "—" },
      {
        id: "year",
        header: "Year",
        cell: info => (
          <Chip label={`Year ${info.row.original.yearOfStudy}`} size="small" style={{ background: "#f1f5f9", color: "#475569", fontWeight: 500, fontSize: "11px" }} />
        )
      },
      { id: "gender", header: "Gender", accessorFn: s => s.gender || "—" },
      { id: "phone", header: "Phone", accessorFn: s => s.phone || "—" },
      {
        id: "status",
        header: "Status",
        cell: info => <Pill tone={STATUS_TONE[info.row.original.status] || "muted"}>{info.row.original.status}</Pill>
      },
      {
        id: "actions",
        header: "Actions",
        cell: info => (
          <Button
            justIcon
            size="sm"
            color="transparent"
            style={{ color: "#64748b" }}
            onClick={() => history.push(`/bwillis/students/${info.row.original.id}`)}
          >
            <Icon>visibility</Icon>
          </Button>
        )
      }
    ];

    const stats = {
        total: total,
        active: students.filter(s => s.status === "active").length,
        deferred: students.filter(s => s.status === "deferred").length,
        completed: students.filter(s => s.status === "completed").length
    };

    return (
      <div>
        {/* ── Hero Section ─────────────────────────────────────────────── */}
        <div className={classes.heroWrap}>
          <div className={classes.heroOverlay} />
          <div className={classes.heroContent}>
            <h1 className={classes.heroTitle}>Student Directory</h1>
            <p className={classes.heroSub}>Manage student records, enrollment status, and academic progress</p>
            <div style={{ marginTop: "24px" }}>
              <Button color="white" className={classes.actionBtn} onClick={this.openDialog}>
                <Icon style={{ marginRight: "8px" }}>person_add</Icon> Register New Student
              </Button>
            </div>
          </div>
        </div>

        {/* ── KPI Stats ────────────────────────────────────────────────── */}
        <div className={classes.kpiRow}>
          {STAT_CONFIGS.map(conf => (
            <div key={conf.key} className={classes.statCard}>
              <div className={classes.iconBox} style={{ background: conf.bg }}>
                <Icon style={{ color: conf.text }}>{conf.icon}</Icon>
              </div>
              <div>
                <h3 className={classes.statNumber}>{conf.key === "total" ? total : stats[conf.key] || 0}</h3>
                <p className={classes.statLabel}>{conf.label}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Toolbar & Filters ────────────────────────────────────────── */}
        <div className={classes.toolbarWrap}>
          <ViewToolbar
            tabs={["All Students", "Active", "Deferred", "Completed"]}
            activeTab={this.state.tab}
            onTabChange={tab => {
              const statusMap = { 0: "", 1: "active", 2: "deferred", 3: "completed" };
              this.setState({ tab, status: statusMap[tab], page: 1 }, this.fetchStudents);
            }}
            actions={[]}
          />
          <form onSubmit={this.handleSearchSubmit} className={classes.searchBar}>
            <CustomInput
              className={classes.searchInput}
              formControlProps={{ style: { margin: 0, paddingTop: 0 } }}
              inputProps={{
                placeholder: "Search by name, student number or phone...",
                value: search,
                onChange: this.handleSearch,
                endAdornment: (
                  <InputAdornment position="end">
                    <Icon style={{ color: "#94a3b8", cursor: "pointer" }} onClick={this.fetchStudents}>search</Icon>
                  </InputAdornment>
                )
              }}
            />
            <select className={classes.filterSelect} value={status} onChange={this.handleFilter("status")}>
              <option value="">All Statuses</option>
              <option value="active">Active</option>
              <option value="deferred">Deferred</option>
              <option value="withdrawn">Withdrawn</option>
              <option value="completed">Completed</option>
            </select>
            <Button color="primary" type="submit" className={classes.actionBtn}>
              Filter Results
            </Button>
          </form>
        </div>

        <GridContainer>
          <GridItem xs={12}>
            {error && <p style={{ color: "#f44336", marginBottom: "12px" }}>{error}</p>}
            <DataTable
              title="Records"
              subtitle={`Showing page ${page} of ${totalPages} (${total} total)`}
              columns={columns}
              data={students}
              isLoading={loading}
              pagination={{ pageIndex: page - 1, pageSize: 20 }}
              onPaginationChange={this.handlePaginationChange}
              totalCount={total}
              emptyState={
                <div className={classes.emptyState}>
                  <Icon style={{ fontSize: "48px", marginBottom: "16px", opacity: 0.3 }}>group_off</Icon>
                  <p>No students found matching your criteria.</p>
                </div>
              }
            />
          </GridItem>
        </GridContainer>

        {/* ── Register Student Dialog ─────────────────────────────────────── */}
        <Dialog open={open} onClose={this.closeDialog} fullWidth maxWidth="md">
          <DialogHeader
            title="Register New Student"
            subtitle="Student No will be auto-generated (PTC/YYYY/NNN)"
          />

          <DialogContent dividers style={{ paddingTop: "8px" }}>
            {formError   && <p className={classes.errorMsg}>⚠ {formError}</p>}
            {formSuccess && <p className={classes.successMsg}>✓ {formSuccess}</p>}

            <p className={classes.sectionTitle}>Personal Information</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="First Name *"
                  id="reg-firstName"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.firstName, onChange: this.handleFormChange("firstName") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Last Name *"
                  id="reg-lastName"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.lastName, onChange: this.handleFormChange("lastName") }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker
                  labelText="Date of Birth"
                  id="reg-dob"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.dob, onChange: this.handleFormChange("dob"), placeholder: "YYYY-MM-DD" }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Gender *</InputLabel>
                  <Select value={form.gender} onChange={this.handleFormChange("gender")}>
                    {GENDERS.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Nationality</InputLabel>
                  <Select value={form.nationality} onChange={this.handleFormChange("nationality")}>
                    {NATIONALITIES.map(n => <MenuItem key={n} value={n}>{n}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Contact</p>
            <GridContainer>
              <GridItem xs={12} sm={4}>
                <CustomInput
                  labelText="Email"
                  id="reg-email"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.email, onChange: this.handleFormChange("email"), type: "email" }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput
                  labelText="Phone"
                  id="reg-phone"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.phone, onChange: this.handleFormChange("phone"), type: "tel" }}
                />
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomInput
                  labelText="Address"
                  id="reg-address"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.address, onChange: this.handleFormChange("address") }}
                />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Guardian / Next of Kin</p>
            <GridContainer>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Guardian Name"
                  id="reg-guardianName"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.guardianName, onChange: this.handleFormChange("guardianName") }}
                />
              </GridItem>
              <GridItem xs={12} sm={6}>
                <CustomInput
                  labelText="Guardian Phone"
                  id="reg-guardianPhone"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.guardianPhone, onChange: this.handleFormChange("guardianPhone"), type: "tel" }}
                />
              </GridItem>
            </GridContainer>

            <p className={classes.sectionTitle}>Academic Placement</p>
            <GridContainer>
              <GridItem xs={12} sm={5}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Programme *</InputLabel>
                  <Select value={form.programmeId} onChange={this.handleFormChange("programmeId")}>
                    {programmes.length === 0 && <MenuItem value="">— No programmes loaded —</MenuItem>}
                    {programmes.map(p => (
                      <MenuItem key={p.id} value={p.id}>{p.name} ({p.code})</MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={3}>
                <FormControl className={classes.formControl}>
                  <InputLabel>Year of Study</InputLabel>
                  <Select value={form.yearOfStudy} onChange={this.handleFormChange("yearOfStudy")}>
                    {[1, 2, 3].map(y => <MenuItem key={y} value={String(y)}>Year {y}</MenuItem>)}
                  </Select>
                </FormControl>
              </GridItem>
              <GridItem xs={12} sm={4}>
                <CustomDatePicker
                  labelText="Enrollment Date"
                  id="reg-enrollmentDate"
                  formControlProps={{ fullWidth: true }}
                  inputProps={{ value: form.enrollmentDate, onChange: this.handleFormChange("enrollmentDate"), placeholder: "YYYY-MM-DD" }}
                />
              </GridItem>
            </GridContainer>
          </DialogContent>

          <DialogFooter>
            <Button outlined onClick={this.closeDialog} disabled={saving}>Cancel</Button>
            <Button color="primary" onClick={this.handleRegister} disabled={saving}>
              {saving ? <CircularProgress size={20} style={{ color: "#fff" }} /> : "Register Student"}
            </Button>
          </DialogFooter>
        </Dialog>
      </div>
    );
  }
}

StudentList.propTypes = { classes: PropTypes.object.isRequired, history: PropTypes.object.isRequired };
export default withRouter(withStyles(styles)(StudentList));
